import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import { hasTrainTransportation } from "app/pages/Listing/productsSelector";
import {
	getFilteredFlights,
	getFiltersCount,
	getVisibleFlights,
	getIncludedFlight,
	getMinUpgradePricesGroupByCabin,
	getCabinFilterValues,
	getAirlinesFilterValues,
	getArrivalAirportFilterValues,
	getDepartureAirportFilterValues,
	getInboundDurationsFilterValues,
	getInboundTakeOffTimeFilterValues,
	getLuggageFilterValues,
	getOutboundDurationsFilterValues,
	getOutboundTakeOffTimeFilterValues,
	getStopCountFilterValues,
	getUpgradePriceFilterValues,
	checkShowFlightFilters,
} from "app/pages/Booking/Quotation/quotationSelectors.js";
import {
	fetchFlightOptions,
	showMoreFlights,
} from "app/pages/Booking/Quotation/quotationActionCreators";
import { scrollToElement } from "app/utils/scroller";
import { getMaxLuggageCount } from "app/pages/Booking/bookingSelectors";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import QuotationFlights from "app/pages/Booking/Quotation/Flight/QuotationFlights";
import FlightsFilterSortContainer from "app/pages/Booking/Quotation/Flight/filter/FlightsFilterSortContainer";
import QuotationTransfersContainer from "app/pages/Booking/Quotation/Transfer/QuotationTransfersContainer";
import QuotationLuggageContainer from "app/pages/Booking/Quotation/Luggage/QuotationLuggageContainer";
import { FLIGHTS_PAGE_SIZE, HTTP_STATUS_CODES } from "app/constants";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { parseQueries } from "app/utils/routerUtils";
import findIndex from "lodash/findIndex";
import find from "lodash/find";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Button } from "app/components/ui/Button/Button";
import { fetchStopoverFlightOptions } from "app/pages/Stopover/stopoverActionCreators";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { Drawer } from "app/components/ui/drawer/Drawer";
import FilterIcon from "app/features/product/productFilters/icons/FilterIcon";
import CabinsFilter from "./filter/CabinsFilter";
import {
	resetFlightsFilter,
	resetFlightsPageSize,
	setFlightsFilterBy,
} from "app/pages/Booking/Quotation/quotationActionCreators";
import { INITIAL_BOOKING } from "app/pages/Booking/bookingReducer";
import classNames from "classnames";

const EMPTY_BAGGAGE = {
	luggage: INITIAL_BOOKING.luggage,
	cabinLuggage: INITIAL_BOOKING.cabinLuggage,
};

const QuotationFlightsContainer = props => {
	const stateProps = useSelector(mapStateToProps, shallowEqual);
	const dispatch = useDispatch();
	const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);

	const {
		brand = {},
		departureDate = 0,
		flights = [],
		visibleFlights = [],
		travelByTrainAvailable = false,
		filteredFlights = [],
		luggageMaxCount = 0,
		updateBooking = () => {},
		showMoreFlights = () => {},
		booking = {},
		quotationCode,
		disableFlightsSelection = false,
		disableBaggagesSelection = false,
		isStopover = false,
		shouldShowFlightFilters = false,
		fetchFlightOptions = () => {},
		fetchStopoverFlightOptions = () => {},
		resolution,
		filtersCount = 0,
		includedFlight,
		minUpgradePricesByCabin,
		filterValues,
		quotation,
		setFlightsFilterBy = () => {},
		resetFlightsPageSize = () => {},
		resetFlightsFilter = () => {},
	} = { ...props, ...stateProps, ...dispatchProps };

	const { track } = useAnalytics();
	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;
	const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
	const selectedFlightCode = booking?.flight?.code;
	const stopoverDuration = booking?.stopoverDuration;
	const query = parseQueries(location.search);
	const { accommodationId } = query;
	const ref = useRef();
	const luggageSectionRef = useRef();
	const selectedFlightRef = useRef(selectedFlightCode);
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (!isStopover) {
			// This hook is to handle flight selection when flights are filtered

			let flightCodeToSelect = visibleFlights.length > 0 ? visibleFlights[0].code : undefined;

			// we check if the selected flight is among the filtered flight
			if (selectedFlightCode && visibleFlights.length > 0) {
				const isSelectedFlightVisible =
					findIndex(visibleFlights, flight => flight.code === selectedFlightCode) !== -1;
				// if so, we keep the flight selection. If not, we select the first flight of filtered flights
				flightCodeToSelect = isSelectedFlightVisible
					? selectedFlightCode
					: visibleFlights[0].code;
			}

			// if the flight to select is different from the already selected one
			if (flightCodeToSelect !== selectedFlightCode && visibleFlights.length > 0) {
				const flight = find(visibleFlights, flight => {
					return flight.code === flightCodeToSelect;
				});

				// then we update the flight selection
				if (flight) {
					updateBooking({ flight, ...EMPTY_BAGGAGE });

					const fetchFlightOptionsFunction = isStopover
						? fetchStopoverFlightOptions
						: fetchFlightOptions;

					const flightOptionsPayload = {
						flight: flight?.code,
						accommodationId,
					};

					if (isStopover) {
						flightOptionsPayload.bookingFlowId = quotationCode;
						flightOptionsPayload.stopOverDuration = stopoverDuration;
					} else {
						flightOptionsPayload.code = quotationCode;
					}

					fetchFlightOptionsFunction(flightOptionsPayload).then(response => {
						const flightOptionsHttpStatus = response.status;

						if (
							!flightOptionsHttpStatus ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.BAD_REQUEST ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.FORBIDDEN
						) {
							track("flightOptions_fail");
						}
					});
				}
			}
		}
	}, [
		visibleFlights,
		selectedFlightCode,
		quotationCode,
		accommodationId,
		isStopover,
		stopoverDuration,
	]);

	useEffect(() => {
		if (isElementOnScreen) {
			track("quote_view_flight");
		}
	}, [isElementOnScreen]);

	// Update the ref whenever selectedFlight changes to always have the latest value
	useEffect(() => {
		selectedFlightRef.current = selectedFlightCode;
	}, [selectedFlightCode]);

	useEffect(() => {
		if (!isMobile) {
			return;
		}

		const defaultCabin = includedFlight?.outbound?.cabin ?? filterValues?.cabin?.[0];

		if (
			quotation?.filterFlightsBy?.cabin === undefined &&
			filterValues?.cabin !== undefined &&
			filterValues?.cabin?.[0] !== undefined &&
			defaultCabin !== undefined
		) {
			setFlightsFilterBy({ cabin: defaultCabin });
		}
	}, [
		isMobile,
		quotation?.filterFlightsBy?.cabin,
		filterValues?.cabin,
		includedFlight?.outbound?.cabin,
		setFlightsFilterBy,
	]);

	const onFlightSelection = useCallback(
		flight => {
			track("quotation_flight_selected", {
				flightTag: flight?.tag,
			});

			if (flight?.code !== selectedFlightRef.current) {
				updateBooking({
					flight,
					...EMPTY_BAGGAGE,
				});

				const fetchFlightOptionsFunction = isStopover
					? fetchStopoverFlightOptions
					: fetchFlightOptions;

				const flightOptionsPayload = {
					flight: flight?.code,
					accommodationId,
				};

				if (isStopover) {
					flightOptionsPayload.bookingFlowId = quotationCode;
					flightOptionsPayload.stopOverDuration = stopoverDuration;
				} else {
					flightOptionsPayload.code = quotationCode;
				}

				fetchFlightOptionsFunction(flightOptionsPayload).then(response => {
					const flightOptionsHttpStatus = response.status;

					if (
						!flightOptionsHttpStatus ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.BAD_REQUEST ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.FORBIDDEN
					) {
						track("flightOptions_fail");
					}
				});
			}
		},
		[quotationCode, accommodationId, isStopover, stopoverDuration, selectedFlightRef.current]
	);

	const handleShowMoreFlights = useCallback(() => {
		showMoreFlights();
		track("flight_show_more", {
			visibleFlightCount: visibleFlights.length + FLIGHTS_PAGE_SIZE,
			filteredFlightCount: filteredFlights.length,
			allFlightCount: flights.length,
		});
	}, [flights.length, visibleFlights.length, filteredFlights.length]);

	const handleLuggageClick = useCallback(() => {
		if (luggageSectionRef.current) {
			scrollToElement({ element: luggageSectionRef.current, disableSmoothScroll: false });
		}
	}, [luggageSectionRef.current]);

	const onClickCabin = useCallback(
		cabin => {
			setFlightsFilterBy({ cabin });
			track("flight_filter_cabin", { value: cabin });
			resetFlightsPageSize();
		},
		[setFlightsFilterBy, resetFlightsPageSize]
	);

	const subtitle = (
		<FormattedMessage
			id={`quotation.${travelByTrainAvailable ? "transport" : "flight"}.subtitle`}
		>
			{message => (
				<Typography
					variant={TYPOGRAPHY_VARIANTS.LARGE}
					isBold
					className="filter-sort__subtitle"
					component="h3"
				>
					{message}
				</Typography>
			)}
		</FormattedMessage>
	);

	return (
		<>
			{((!disableFlightsSelection && flights.length > 0) || !disableBaggagesSelection) && (
				<div className="quotation__item" ref={ref}>
					{travelByTrainAvailable ? (
						<FormattedMessage
							id={
								disableFlightsSelection
									? "quotation.flight.baggage.only.title"
									: "quotation.transport.title"
							}
						>
							{message => (
								<div className="quotation__headline">
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL3}
										variantMD={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="quotation__title"
										component="h2"
									>
										{message}
									</Typography>
								</div>
							)}
						</FormattedMessage>
					) : (
						<FormattedMessage
							id={
								disableFlightsSelection
									? "quotation.flight.baggage.only.title"
									: "quotation.flight.title"
							}
						>
							{message => (
								<div className="quotation__headline">
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL}
										variantMD={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="quotation__title"
										component="h2"
									>
										{message}
									</Typography>
								</div>
							)}
						</FormattedMessage>
					)}
					{!disableFlightsSelection && (
						<>
							{isMobile ? (
								<div className="quotation__filter-sort-mobile">
									{subtitle}
									{shouldShowFlightFilters && (
										<Drawer.Root
											open={isFilterDrawerOpen}
											onOpenChange={setIsFilterDrawerOpen}
											side="right"
											ariaLabel="Flight filters"
										>
											<Drawer.Trigger>
												<Button
													startIcon={<FilterIcon className="tune-icon" />}
													type="button"
													className={classNames(
														"quotation__filter-trigger-btn",
														{
															"quotation__filter-trigger-btn--has-filters":
																filtersCount > 0,
														}
													)}
												>
													<Typography
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
														isBold
													>
														<FormattedMessage id="quotation.flights.filter.mobile.button">
															{message =>
																`${message}${
																	filtersCount > 0
																		? ` (${filtersCount})`
																		: ""
																}`
															}
														</FormattedMessage>
													</Typography>
												</Button>
											</Drawer.Trigger>
											<Drawer.Content className="quotation__filter-drawer">
												<Drawer.Header className="quotation__filter-drawer-header">
													<Typography
														variant={TYPOGRAPHY_VARIANTS.XL}
														isBold
														component="span"
														className="quotation__filter-drawer-title"
													>
														<FormattedMessage id="quotation.flights.filter.mobile.button" />
													</Typography>
													{filtersCount > 0 && (
														<Button
															variant="borderless"
															size="sm"
															onClick={() => {
																resetFlightsFilter(filterValues);
																resetFlightsPageSize();
																track("flight_filter_reset");
															}}
															className="filter-sort__mobile-header-reset"
														>
															<Typography
																variant={
																	TYPOGRAPHY_VARIANTS.REGULAR
																}
																isBold
															>
																<FormattedMessage id="general.filter.raz.label" />
															</Typography>
														</Button>
													)}
												</Drawer.Header>
												<Drawer.Body>
													<FlightsFilterSortContainer
														hideCabins
														isMobileDrawer
													/>
												</Drawer.Body>
												<Drawer.Footer
													className={classNames(
														"quotation__filter-drawer-footer",
														{
															"stopover-side-panel__footer":
																isStopover,
														}
													)}
												>
													<Button
														variant="primary"
														size="lg"
														fullWidth
														onClick={() => setIsFilterDrawerOpen(false)}
													>
														<FormattedMessage
															id="product.filter.submit"
															values={{
																count: filteredFlights.length,
															}}
														/>
													</Button>
												</Drawer.Footer>
											</Drawer.Content>
										</Drawer.Root>
									)}
									{filterValues?.cabin &&
										filterValues?.cabin?.length > 1 &&
										filteredFlights.length > 0 && (
											<div className="filter-sort__cabins">
												<CabinsFilter
													includedFlight={includedFlight}
													minUpgradePricesByCabin={
														minUpgradePricesByCabin
													}
													cabins={filterValues?.cabin}
													selectedCabin={
														quotation?.filterFlightsBy?.cabin
													}
													onSelect={onClickCabin}
												/>
											</div>
										)}
								</div>
							) : (
								<div className="quotation__filter-sort">
									<FlightsFilterSortContainer
										subtitle={subtitle}
										hideCabins={visibleFlights.length <= 0}
									/>
								</div>
							)}
							<div className="quotation__content">
								<QuotationFlights
									travelByTrainAvailable={travelByTrainAvailable}
									flights={visibleFlights}
									luggageMaxCount={luggageMaxCount}
									onLuggageClick={handleLuggageClick}
									onChange={onFlightSelection}
									departureDate={departureDate}
									brand={brand.code}
								/>

								{visibleFlights.length < visibleFlights.flightsPageSize ||
								visibleFlights.length === filteredFlights.length ||
								visibleFlights.length === flights.length ? (
									false
								) : (
									<div className="quotation__show-more">
										<Button
											variant="secondary"
											onClick={handleShowMoreFlights}
											data-testid="show-more-flight-button"
										>
											<FormattedMessage id="general.more.options.button.label" />
										</Button>
									</div>
								)}
							</div>
						</>
					)}

					{!disableBaggagesSelection && visibleFlights.length > 0 && (
						<div className="quotation__luggages" ref={luggageSectionRef}>
							<QuotationLuggageContainer
								disableFlightSelection={disableFlightsSelection}
							/>
						</div>
					)}
				</div>
			)}
			<QuotationTransfersContainer />
		</>
	);
};

QuotationFlightsContainer.propTypes = {
	departureDate: PropTypes.number,
	brand: PropTypes.object,
	flights: PropTypes.array,
	visibleFlights: PropTypes.array,
	isStopover: PropTypes.bool,
	disableFlightsSelection: PropTypes.bool,
	disableBaggagesSelection: PropTypes.bool,
	travelByTrainAvailable: PropTypes.bool,
	shouldShowFlightFilters: PropTypes.bool,
	filteredFlights: PropTypes.array,
	luggageMaxCount: PropTypes.number,
	updateBooking: PropTypes.func,
	showMoreFlights: PropTypes.func,
	booking: PropTypes.object,
	quotationCode: PropTypes.string,
	fetchFlightOptions: PropTypes.func,
	fetchStopoverFlightOptions: PropTypes.func,
	resolution: PropTypes.string,
	filtersCount: PropTypes.number,
	includedFlight: PropTypes.object,
	minUpgradePricesByCabin: PropTypes.object,
	filterValues: PropTypes.object,
	quotation: PropTypes.object,
	setFlightsFilterBy: PropTypes.func,
	resetFlightsPageSize: PropTypes.func,
	resetFlightsFilter: PropTypes.func,
};

const mapStateToProps = state => ({
	departureDate: state.booking.departureDate,
	brand: state.brand,
	flights: state.quotation.flights,
	visibleFlights: getVisibleFlights(state),
	travelByTrainAvailable: hasTrainTransportation(state),
	filteredFlights: getFilteredFlights(state),
	filtersCount: getFiltersCount(state),
	luggageMaxCount: getMaxLuggageCount(state),
	quotationCode: state.quotation.code,
	booking: state.booking,
	resolution: state.resolution,
	includedFlight: getIncludedFlight(state),
	minUpgradePricesByCabin: getMinUpgradePricesGroupByCabin(state),
	shouldShowFlightFilters: checkShowFlightFilters(state),
	filterValues: {
		stopsCount: getStopCountFilterValues(state),
		departureAirport: getDepartureAirportFilterValues(state),
		arrivalAirport: getArrivalAirportFilterValues(state),
		cabin: getCabinFilterValues(state),
		airline: getAirlinesFilterValues(state),
		outboundDuration: getOutboundDurationsFilterValues(state),
		inboundDuration: getInboundDurationsFilterValues(state),
		inboundTakeOffTime: getInboundTakeOffTimeFilterValues(state),
		outboundTakeOffTime: getOutboundTakeOffTimeFilterValues(state),
		upgradePrice: getUpgradePriceFilterValues(state),
		luggage: getLuggageFilterValues(state),
	},
	quotation: state.quotation,
});

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			updateBooking,
			showMoreFlights,
			fetchFlightOptions,
			fetchStopoverFlightOptions,
			setFlightsFilterBy,
			resetFlightsPageSize,
			resetFlightsFilter,
		},
		dispatch
	);
};

export default QuotationFlightsContainer;
